import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ObjectException } from 'util/errors';

import { isNotValidObjectID } from 'functions/stringValidation';
import services from 'api/services';
const { ConfigService, FreecodeService, FreecodeCreatorService } = services;

const validateTestAndImplementation = (response) => {
  if (!response?.test?._id) throw new ObjectException('Test missing from response', 'VALIDATION_ERROR');
  if (!response?.testImpl?._id) throw new ObjectException('Implementation missing from response', 'VALIDATION_ERROR');
  if (response?.test?._id !== response?.testImpl?.test)
    throw new ObjectException('Test and implementation misssmatch', 'VALIDATION_ERROR');
};

export const useLoadFreecodeTicketInspect = (inputId) => {
  const { isInitialized } = useSelector((state) => state.auth);
  const [freecodeResponse, setFreecodeResponse] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);
  useEffect(() => {
    if (!isInitialized) return;
    const fetchData = async () => {
      try {
        if (isNotValidObjectID(inputId)) throw new ObjectException(`Not a valid id ${inputId}`, 'VALIDATION_ERROR');

        const response = await FreecodeService.loadFreecodeTicketInspect(inputId);
        validateTestAndImplementation(response);
        setFreecodeResponse(response);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [inputId, isInitialized]);
  return [freecodeResponse, loading, error];
};

export const useLoadFreecodeCreator = (testId, testImplId) => {
  const { isInitialized } = useSelector((state) => state.auth);
  const [freecodeResponse, setFreecodeResponse] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);
  useEffect(() => {
    if (!isInitialized) return;
    const fetchData = async () => {
      try {
        if (isNotValidObjectID(testId)) throw new ObjectException(`Not a valid id ${testId}`, 'VALIDATION_ERROR');
        if (testImplId && isNotValidObjectID(testImplId))
          throw new ObjectException(`Not a valid id ${testImplId}`, 'VALIDATION_ERROR');

        const response = await FreecodeService.loadFreecodeCreator(testId, testImplId);
        validateTestAndImplementation(response);
        setFreecodeResponse(response);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [testId, testImplId, isInitialized]);
  return [freecodeResponse, loading, error];
};

export const useFreecodeCreatorImplVersions = (testId) => {
  const { isInitialized } = useSelector((state) => state.auth);
  const [implVersions, setImplVersions] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);
  useEffect(() => {
    if (!isInitialized) return;
    const fetchData = async () => {
      try {
        if (isNotValidObjectID(testId)) throw new ObjectException(`Not a valid id ${testId}`, 'VALIDATION_ERROR');
        const response = await FreecodeCreatorService.loadImplementationVersions(testId);
        setImplVersions(response);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [testId, isInitialized]);
  return [implVersions, loading, error];
};

export const useLoadFreecodeTakeExercise = (exerciseId, courseId) => {
  const { isInitialized } = useSelector((state) => state.auth);
  const [freecodeResponse, setFreecodeResponse] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);
  useEffect(() => {
    if (!isInitialized) return;
    const fetchData = async () => {
      try {
        if (isNotValidObjectID(exerciseId))
          throw new ObjectException(`Not a valid id ${exerciseId}`, 'VALIDATION_ERROR');
        if (courseId && isNotValidObjectID(courseId))
          throw new ObjectException(`Not a valid id ${courseId}`, 'VALIDATION_ERROR');

        const response = await FreecodeService.loadFreecodeTakeExercise(exerciseId, courseId);
        validateTestAndImplementation(response);
        setFreecodeResponse(response);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [exerciseId, courseId, isInitialized]);
  return [freecodeResponse, loading, error];
};

export const useLoadFreecodeContest = (contestId) => {
  const { isInitialized } = useSelector((state) => state.auth);
  const [freecodeResponse, setFreecodeResponse] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);
  useEffect(() => {
    if (!isInitialized) return;
    const fetchData = async () => {
      try {
        if (isNotValidObjectID(contestId)) throw new ObjectException(`Not a valid id ${contestId}`, 'VALIDATION_ERROR');

        const response = await FreecodeService.loadFreecodeContest(contestId);
        validateTestAndImplementation(response);
        if (response.contest?.test !== response.test._id)
          throw new ObjectException('Test and contest misssmatch', 'VALIDATION_ERROR');

        setFreecodeResponse(response);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [contestId, isInitialized]);
  return [freecodeResponse, loading, error];
};

export const useLoadFreecodeTrialTest = () => {
  const [freecodeResponse, setFreecodeResponse] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await FreecodeService.loadFreecodeTrialTest();
        validateTestAndImplementation(response);
        setFreecodeResponse(response);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  return [freecodeResponse, loading, error];
};

// Take ticket loads languageVersions first since if that fails we do not want to load the ticket
export const useLoadFreecodeTakeTicket = (ticketId) => {
  const { isInitialized } = useSelector((state) => state.auth);
  const [freecodeResponse, setFreecodeResponse] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);
  useEffect(() => {
    if (!isInitialized) return;
    const fetchData = async () => {
      try {
        if (isNotValidObjectID(ticketId)) throw new ObjectException(`Not a valid id ${ticketId}`, 'VALIDATION_ERROR');

        const languageVersions = await ConfigService.getLanguageVersions();
        if (!languageVersions?.value?.[4]?.python)
          throw new ObjectException(`Failed to fetch language versions`, 'VALIDATION_ERROR');

        const response = await FreecodeService.loadFreecodeTakeTicket(ticketId);
        validateTestAndImplementation(response);
        if (response.ticket?.test !== response.test._id)
          throw new ObjectException('Test and ticket misssmatch', 'VALIDATION_ERROR');

        setFreecodeResponse({
          languageVersions: languageVersions.value,
          freecodeResponse: response,
        });
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ticketId, isInitialized]);
  return [freecodeResponse, loading, error];
};
